/**
 * Single source for outbound social / AMA links.
 * Homepage Finder status bar, Connect folder, and JSON-LD `sameAs` all read this.
 */

export type SocialLinkId =
  | 'ama'
  | 'instagram'
  | 'x'
  | 'github'
  | 'linkedin'
  | 'unsplash'
  | 'spotify'
  | 'hn'
  | 'fiverr'
  | 'facebook';

export interface SocialLink {
  id: SocialLinkId;
  label: string;
  href: string;
  /** Handle shown on Connect Cover Flow tiles */
  handle: string;
  /** Brand color for Connect-folder file tiles */
  color: string;
  /** Canonical profile URL for JSON-LD sameAs (omit for Discord AMA) */
  sameAs?: string;
}

export const SOCIAL_LINKS: readonly SocialLink[] = [
  {
    id: 'ama',
    label: 'AMA @ DTW',
    handle: '@DTW',
    href: 'https://discord.gg/JemYHj8',
    color: '#5865F2',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    handle: '@cdbattags',
    href: 'https://instagram.com/cdbattags',
    color: '#E4405F',
    sameAs: 'https://instagram.com/cdbattags',
  },
  {
    id: 'x',
    label: 'X',
    handle: '@cdbattags',
    href: 'https://x.com/cdbattags',
    color: '#0f1419',
    sameAs: 'https://twitter.com/cdbattags',
  },
  {
    id: 'github',
    label: 'GitHub',
    handle: '@cdbattags',
    href: 'https://github.com/cdbattags',
    color: '#24292f',
    sameAs: 'https://github.com/cdbattags',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    handle: 'in/cdbattags',
    href: 'https://linkedin.com/in/cdbattags',
    color: '#0A66C2',
    sameAs: 'https://www.linkedin.com/in/cdbattags/',
  },
  {
    id: 'unsplash',
    label: 'Unsplash',
    handle: '@cdbattags',
    href: 'https://unsplash.com/@cdbattags?grid=single&sort_by=popular',
    color: '#111111',
    sameAs: 'https://unsplash.com/@cdbattags',
  },
  {
    id: 'spotify',
    label: 'Spotify',
    handle: 'cdbattags',
    href: 'https://open.spotify.com/user/1229186524',
    color: '#1DB954',
    sameAs: 'https://open.spotify.com/user/1229186524',
  },
  {
    id: 'hn',
    label: 'Hacker News',
    handle: 'cdbattags',
    href: 'https://news.ycombinator.com/user?id=cdbattags',
    color: '#ff6600',
    sameAs: 'https://news.ycombinator.com/user?id=cdbattags',
  },
  {
    id: 'fiverr',
    label: 'Fiverr',
    handle: 'bundlr',
    href: 'https://www.fiverr.com/bundlr',
    color: '#1DBF73',
    sameAs: 'https://www.fiverr.com/bundlr',
  },
  {
    id: 'facebook',
    label: 'Facebook',
    handle: 'cdbattags',
    href: 'https://www.facebook.com/cdbattags',
    color: '#1877F2',
    sameAs: 'https://www.facebook.com/cdbattags',
  },
] as const;

export function isSocialLinkId(id: string): id is SocialLinkId {
  return SOCIAL_LINKS.some((link) => link.id === id);
}

export function socialSameAsUrls(): string[] {
  return SOCIAL_LINKS.map((link) => link.sameAs).filter((url): url is string => Boolean(url));
}
